import { ArrowRight, Check, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { sectionCls } from '@/pages/launch/helpers';

interface LaunchReportSectionProps {
  sectionRef: React.Ref<HTMLDivElement>;
  visible: boolean;
  reportEmail: string;
  setReportEmail: (email: string) => void;
  reportSubmitted: boolean;
  reportLoading: boolean;
  onSubmitReport: () => void;
}

const reportStats = [
  {
    stat: '69%',
    label: 'of venues use 4+ disconnected tools',
    source: 'Toast, 2019',
  },
  {
    stat: '20%',
    label: 'of bar product lost to shrinkage on average',
    source: 'BevInco, 5,000+ audits',
  },
  {
    stat: '26\u00D7',
    label: 'more valuable \u2014 repeat guests vs. one-timers',
    source: 'Bloom Intelligence',
  },
  {
    stat: '25\u201395%',
    label: 'profit boost from a 5% lift in retention',
    source: 'Bain & Company',
  },
];

const LaunchReportSection = ({
  sectionRef,
  visible,
  reportEmail,
  setReportEmail,
  reportSubmitted,
  reportLoading,
  onSubmitReport,
}: LaunchReportSectionProps) => (
  <section
    ref={sectionRef}
    className={`relative z-10 py-16 sm:py-24 px-4 sm:px-6 overflow-hidden ${sectionCls(visible)}`}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground to-foreground/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.15),transparent_60%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.08),transparent_50%)]" />

    <div className="relative max-w-5xl mx-auto">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <Badge className="mb-5 bg-primary/20 text-primary border-primary/30 hover:bg-primary/25 text-xs font-medium tracking-wide">
            Free Industry Report
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4 text-background leading-tight">
            The Hidden Cost of
            <br />
            Disconnected Venue Technology
          </h2>
          <p className="text-background/60 mb-8 leading-relaxed text-sm sm:text-base max-w-md">
            We fact-checked every claim in our pitch deck against independent data. Here's what we
            found.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {reportStats.map((item) => (
              <div
                key={item.stat}
                className="rounded-xl bg-background/[0.06] border border-background/10 px-4 py-4 hover:bg-background/[0.09] transition-colors"
              >
                <p className="text-2xl font-display font-bold text-primary leading-none mb-1.5">
                  {item.stat}
                </p>
                <p className="text-[13px] text-background/70 leading-snug">{item.label}</p>
                <p className="text-[10px] text-background/30 mt-1.5 uppercase tracking-wider">
                  {item.source}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div>
          {reportSubmitted ? (
            <Card className="p-8 bg-background/[0.08] border-background/15 text-center backdrop-blur-md rounded-2xl shadow-2xl shadow-primary/5">
              <div className="h-14 w-14 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-5">
                <Check size={28} className="text-success" />
              </div>
              <p className="font-semibold text-background text-lg mb-2">Your report is ready!</p>
              <a href="/reports/hidden-cost-of-disconnected-technology.pdf" download>
                <Button
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground mt-3 h-12"
                >
                  <Download size={16} className="mr-2" /> Download Report (PDF)
                </Button>
              </a>
            </Card>
          ) : (
            <Card className="p-8 bg-background/[0.08] border-background/15 backdrop-blur-md rounded-2xl shadow-2xl shadow-primary/5">
              <div className="mb-5">
                <p className="text-background font-semibold text-lg mb-1">Get the full report</p>
                <p className="text-sm text-background/50">
                  22 independent sources. Every number verified.
                </p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmitReport();
                }}
                className="space-y-4"
              >
                <Input
                  type="email"
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                  placeholder="you@venue.com"
                  className="bg-background/10 border-background/20 text-background placeholder:text-background/40 focus-visible:ring-primary h-12 rounded-xl"
                  required
                />
                <Button
                  type="submit"
                  disabled={reportLoading}
                  size="lg"
                  className="w-full h-12 rounded-xl text-base"
                >
                  {reportLoading ? 'Sending\u2026' : 'Get Free Report'}{' '}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </form>
            </Card>
          )}
        </div>
      </div>
    </div>
  </section>
);

export default LaunchReportSection;
